import { acceptHMRUpdate, defineStore } from 'pinia'
import { createVNode, render, toRaw } from 'vue'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { PathLayer, IconLayer } from '@deck.gl/layers'
import app from '@/main'
import { getVehicles, getVehiclesPositions } from '../scripts/task'
import { isValidObject } from '../helpers/objectHelper'
import { formatDate, toUtcNaiveDateTime, toUtcNaiveDateTimeInclusiveEndDate } from '../helpers/dateHelper'
import { hexToRgba } from '../helpers/yieldMapBuckets'
import { segmentBearingAtPoint } from '../helpers/bearingHelper'
import { bearingToIconAngle, getVehicleGpsArrowIcon } from '../helpers/vehicleGpsArrowIcon'
import { ensureVehicleFieldModeIcon, fieldModeIconAngle, vehicleIconMapSizeScale } from '../helpers/vehicleFieldModeIcon'
import { useMaps } from './maps'
import VehiclePositionInfoWindow from '@/components/map/VehiclePositionInfoWindow.vue'
import { usePlanner } from '@/stores/planner'
import { useDemoStore } from '@/stores/demoStore'

const LIVE_POLL_INTERVAL_MS = 30000
const LIVE_VEHICLE_ICON_SIZE_PX = 48
const HISTORY_END_VEHICLE_ICON_SIZE_PX = 40

const STREET_STEER_MODES = new Set([5, 11])
const SWITCH_OVER_STEER_MODES = new Set([6, 10])

export const STEER_MODE_LABELS = {
    1: 'Field 4-wheel steering',
    2: 'Field back-wheel steering',
    3: 'Headland 4-wheel steering',
    4: 'Headland back-wheel steering',
    5: 'Street 4-wheel steering',
    6: 'Street/Field switch-over',
    7: 'Field sprayer mode',
    8: 'Spread wheels 1 and 4',
    9: 'Spread wheels 2 and 3',
    10: 'Street/Field switch-over with support stand',
    11: 'Street true path',
    12: 'Tank steering',
}

export { LIVE_POLL_INTERVAL_MS }

const GPS_ROUTE_POINT_STATES = [
    {
        id: 'implement_working_with_autopilot',
        color: '#4ade80',
        label: 'Implement working with autopilot',
        matches: (point) => point.implement_working === true && point.autopilot_on === true,
    },
    {
        id: 'implement_working_without_autopilot',
        color: '#f97316',
        label: 'Implement working without autopilot',
        matches: (point) => point.implement_working === true,
    },
    {
        id: 'autopilot_only',
        color: '#7c3aed',
        label: 'Autopilot only',
        matches: (point) => point.autopilot_on === true,
    },
    {
        id: 'normal',
        color: '#0F172A',
        label: 'Normal',
        matches: () => true,
    },
]

const GPS_ROUTE_LEGEND_STATE_IDS = [
    'autopilot_only',
    'implement_working_without_autopilot',
    'implement_working_with_autopilot',
]

function getGpsRoutePointState(point) {
    return GPS_ROUTE_POINT_STATES.find((state) => state.matches(point))
        ?? GPS_ROUTE_POINT_STATES[GPS_ROUTE_POINT_STATES.length - 1]
}

export const useVehiclesLiveStore = defineStore('vehiclesLive', {
    state: () => ({
        livePollIntervalMs: LIVE_POLL_INTERVAL_MS,
        vehicleSerials: [],
        selectedCustomer: null,
        selectedDataMode: null,
        startDate: null,
        endDate: null,
        historyVehicles: [],
        liveVehicles: [],
        liveVehicleStatus: {},
        loadingVehicleSerial: null,
        loadingVehicles: false,
        visibleOnMapMachineId: null,
        liveOverlay: null,
        routeOverlay: null,
        gpsInfoWindow: null,
        gpsInfoWindowEl: null,
        gpsMapClickListener: null,
        legendDraggable: null,
        dataPollIntervalId: null,
        dataPollInFlight: false,
        liveOnFieldsMap: false,
        fieldsMapLiveLoading: false,
        liveOnVehicleTrackingPage: false,
        dataModes: {
            LIVE: { name: 'Live', value: 'live' },
            HISTORY: { name: 'History', value: 'history' },
            LIVE_HISTORY: { name: 'Live with history', value: 'live_history' }
        },
        requestedIsobusTaskId: null,
        paths: [],
        selectedPathId: null,
        pathsLoading: false,
    }),
    getters: {
        gpsRouteLegend() {
            return GPS_ROUTE_LEGEND_STATE_IDS.map((id) => {
                const state = GPS_ROUTE_POINT_STATES.find((item) => item.id === id)
                return { color: state.color, label: state.label }
            })
        },
    },
    actions: {
        formatNumber(value, decimals, suffix = '') {
            const number = Number(value)
            if (!Number.isFinite(number)) {
                return null
            }
            return `${number.toFixed(decimals)}${suffix}`
        },
        formatDateTime(value) {
            if (!value) {
                return null
            }
            const d = formatDate(value)
            return `${d.date}, ${d.time} UTC`
        },
        boolLabel(value) {
            if (value == null) {
                return 'N/A'
            }
            return value ? 'Yes' : 'No'
        },
        onlineStatusLabelKey(isOnline) {
            if (isOnline === true) {
                return 'Online'
            }
            if (isOnline === false) {
                return 'Offline'
            }
            return null
        },
        onlineStatusClass(isOnline) {
            if (isOnline === true) {
                return 'status-badge--online'
            }
            if (isOnline === false) {
                return 'status-badge--offline'
            }
            return 'status-badge--unknown'
        },
        getSteerModeLabel(value) {
            const label = STEER_MODE_LABELS[value]
            return label ?? `Unknown (${value})`
        },
        vehicleIconFromSteerMode(steermode) {
            const mode = Number(steermode)

            if (STREET_STEER_MODES.has(mode)) {
                return 'street'
            }
            if (SWITCH_OVER_STEER_MODES.has(mode)) {
                return 'switch_over'
            }
            return 'field'
        },
        vehicleIconMapSize(steermode, baseSize) {
            return baseSize * vehicleIconMapSizeScale(this.vehicleIconFromSteerMode(steermode))
        },
        normalizeVehicleGpsPoints(payload) {
            if (!payload?.points?.length) {
                return []
            }

            const machineId = payload.machine_id ?? null

            return payload.points.map((item, index) => {
                const lat = Number(item.latitude ?? item.lat)
                const lng = Number(item.longitude ?? item.lng ?? item.lon)
                const speed = Number(item.speed_mps)
                const gpsSpeed = Number(item.gps_speed_mps ?? item.gps_speed)
                const heading = Number(item.heading)

                return {
                    index,
                    timestamp: item.timestamp ?? item.time ?? '',
                    machine_id: machineId,
                    latitude: lat,
                    longitude: lng,
                    altitude: item.altitude ?? null,
                    speed_mps: Number.isFinite(speed) ? speed : null,
                    gps_speed_mps: Number.isFinite(gpsSpeed) ? gpsSpeed : null,
                    heading: Number.isFinite(heading) ? heading : null,
                    autopilot_on: item.autopilot_on,
                    implement_working: item.implement_working,
                    worked_in_bucket: item.worked_in_bucket,
                    steermode: item.steermode ?? null,
                    isobus_task_id: item.isobus_task_id ?? null,
                    implement_name: item.implement_name ?? null,
                    subimplements: item.subimplements ?? null,
                }
            })
        },
        getGpsPointColor(point) {
            return getGpsRoutePointState(point).color
        },
        getGpsPointStateLabel(point) {
            return getGpsRoutePointState(point).label
        },
        buildRouteArrowPoints(points) {
            return points.map((point, index) => ({
                ...point,
                segmentBearing: segmentBearingAtPoint(points, index),
            }))
        },
        async loadVehicles() {
            if (!this.selectedCustomer) {
                return false
            }

            this.loadingVehicles = true
            this.vehicleSerials = []
            this.historyVehicles = []
            this.liveVehicles = []
            this.liveVehicleStatus = {}
            this.clearMapOverlays()

            try {
                const vehicles = await getVehicles()
                if (isValidObject(vehicles)) {
                    const customerVehicles = vehicles[this.selectedCustomer.id] || []
                    this.vehicleSerials = customerVehicles.map(vehicle => vehicle.vehicle_serial)
                    return true
                }

                this.vehicleSerials = []
                return false
            } catch (error) {
                console.error('Error getting vehicles:', error)
                return false
            } finally {
                this.loadingVehicles = false
            }
        },
        async loadLiveVehicles() {
            this.liveOnFieldsMap = false
            return this.loadLivePositions()
        },
        isLiveMarkersMode() {
            if (this.liveOnFieldsMap) {
                return true
            }

            return this.liveOnVehicleTrackingPage
                && this.selectedDataMode === this.dataModes.LIVE.value
        },
        async loadLivePositions() {
            if (this.vehicleSerials.length === 0) {
                return false
            }

            this.clearMapOverlays()
            this.liveVehicles = []
            this.liveVehicleStatus = Object.fromEntries(
                this.vehicleSerials.map((vehicleSerial) => [vehicleSerial, 'loading']),
            )

            await Promise.all(this.vehicleSerials.map((vehicleSerial) => this.loadLiveVehicle(vehicleSerial)))

            if (!this.isLiveMarkersMode()) {
                return false
            }

            await this.refreshLiveMarkersOverlay()
            this.startDataPolling()
            return true
        },
        async startFieldsMapLive(customer) {
            const demo = useDemoStore()
            if (demo.isBlocked('vehicles')) return false

            if (!customer) {
                this.stopFieldsMapLive()
                return false
            }

            if (this.fieldsMapLiveLoading) {
                return false
            }

            if (
                this.liveOnFieldsMap
                && this.selectedCustomer?.id === customer.id
                && this.liveVehicles.length > 0
            ) {
                await this.refreshLiveMarkersOverlay()
                return true
            }

            this.liveOnFieldsMap = true
            this.fieldsMapLiveLoading = true
            this.selectedCustomer = customer

            try {
                const loaded = await this.loadVehicles()
                if (!this.liveOnFieldsMap) {
                    return false
                }

                if (!loaded || this.vehicleSerials.length === 0) {
                    this.stopFieldsMapLive()
                    return false
                }

                return await this.loadLivePositions()
            } catch (error) {
                console.error('Error loading live vehicles on fields map:', error)
                this.stopFieldsMapLive()
                return false
            } finally {
                this.fieldsMapLiveLoading = false
            }
        },
        stopFieldsMapLive() {
            if (!this.liveOnFieldsMap && !this.fieldsMapLiveLoading) {
                return
            }

            this.liveOnFieldsMap = false
            this.fieldsMapLiveLoading = false

            const mapStore = useMaps()
            this.clearLiveOverlay()
            mapStore.showFieldFills()
            this.closePointInfoWindow()
            this.removeGpsMapClickListener()
            this.stopDataPolling()
        },
        async fetchLiveVehicleEntry(vehicleSerial) {
            const payload = await getVehiclesPositions(vehicleSerial)
            const points = this.normalizeVehicleGpsPoints(payload)
            const machineId = payload?.machine_id ?? vehicleSerial

            return {
                machineId,
                vehicleSerial,
                payload,
                points,
            }
        },
        upsertLiveVehicle(entry) {
            this.liveVehicles = [
                ...this.liveVehicles.filter((item) => item.vehicleSerial !== entry.vehicleSerial),
                entry,
            ]
        },
        async loadLiveVehicle(vehicleSerial, { silent = false } = {}) {
            try {
                const entry = await this.fetchLiveVehicleEntry(vehicleSerial)

                this.upsertLiveVehicle(entry)

                if (!entry.points.length) {
                    this.liveVehicles = this.liveVehicles.filter(
                        (item) => item.vehicleSerial !== entry.vehicleSerial,
                    )
                }

                this.liveVehicleStatus[vehicleSerial] = 'loaded'
                return true
            } catch (error) {
                console.error('Error getting vehicle live position:', error)
                if (!silent) {
                    this.liveVehicleStatus[vehicleSerial] = 'error'
                }
                return false
            }
        },
        async refreshLiveVehiclesSilent() {
            if (!this.isLiveMarkersMode()) {
                return false
            }

            if (this.vehicleSerials.length === 0) {
                this.stopDataPolling()
                return false
            }

            if (this.dataPollInFlight) {
                return false
            }

            this.dataPollInFlight = true

            try {
                await Promise.all(
                    this.vehicleSerials.map((vehicleSerial) => this.loadLiveVehicle(vehicleSerial, { silent: true })),
                )
                await this.refreshLiveMarkersOverlay()
                return true
            } finally {
                this.dataPollInFlight = false
            }
        },
        getLiveVehicle(vehicleSerial) {
            return this.liveVehicles.find((item) => item.vehicleSerial === vehicleSerial) ?? null
        },
        getLiveVehicleStatus(vehicleSerial) {
            return this.liveVehicleStatus[vehicleSerial] ?? null
        },
        async fetchVehicleHistoryEntry(vehicleSerial) {
            if (this.selectedDataMode === this.dataModes.LIVE_HISTORY.value && !this.startDate) {
                return null
            }

            if (this.selectedDataMode !== this.dataModes.LIVE_HISTORY.value && (!this.startDate || !this.endDate)) {
                return null
            }

            const startTime = toUtcNaiveDateTime(this.startDate)
            const endTime = this.selectedDataMode === this.dataModes.LIVE_HISTORY.value
                ? null
                : toUtcNaiveDateTimeInclusiveEndDate(this.endDate)

            const payload = await getVehiclesPositions(vehicleSerial, startTime, endTime)
            const points = this.normalizeVehicleGpsPoints(payload)
            const machineId = payload?.machine_id ?? vehicleSerial

            return {
                machineId,
                vehicleSerial,
                payload,
                points,
            }
        },
        upsertHistoryVehicle(entry) {
            const existingIdx = this.historyVehicles.findIndex(
                (item) => item.vehicleSerial === entry.vehicleSerial,
            )
            if (existingIdx >= 0) {
                this.historyVehicles[existingIdx] = entry
            } else {
                this.historyVehicles.push(entry)
            }
        },
        async loadVehicleHistory(vehicleSerial) {
            this.loadingVehicleSerial = vehicleSerial

            try {
                const entry = await this.fetchVehicleHistoryEntry(vehicleSerial)
                if (!entry) {
                    return false
                }

                this.upsertHistoryVehicle(entry)

                if (entry.points.length) {
                    await this.showVehicleOnMap(entry.machineId)
                }

                return true
            } catch (error) {
                console.error('Error getting vehicle positions:', error)
                return false
            } finally {
                this.loadingVehicleSerial = null
            }
        },
        async refreshVisibleLiveHistoryVehicle() {
            if (this.selectedDataMode !== this.dataModes.LIVE_HISTORY.value) {
                return false
            }

            if (!this.visibleOnMapMachineId || !this.startDate) {
                this.stopDataPolling()
                return false
            }

            if (this.dataPollInFlight) {
                return false
            }

            const visibleVehicle = this.historyVehicles.find(
                (item) => item.machineId === this.visibleOnMapMachineId,
            )
            if (!visibleVehicle) {
                this.stopDataPolling()
                return false
            }

            this.dataPollInFlight = true

            try {
                const entry = await this.fetchVehicleHistoryEntry(visibleVehicle.vehicleSerial)
                if (!entry) {
                    return false
                }

                this.upsertHistoryVehicle(entry)

                if (this.visibleOnMapMachineId === entry.machineId) {
                    await this.drawHistoryRoute(entry)
                }

                return true
            } catch (error) {
                console.error('Error refreshing live history vehicle:', error)
                return false
            } finally {
                this.dataPollInFlight = false
            }
        },
        shouldRunDataPolling() {
            if (this.isLiveMarkersMode()) {
                return this.vehicleSerials.length > 0
            }

            if (this.selectedDataMode === this.dataModes.LIVE_HISTORY.value) {
                return !!this.visibleOnMapMachineId && !!this.startDate
            }

            return false
        },
        tickDataPoll() {
            if (this.isLiveMarkersMode()) {
                this.refreshLiveVehiclesSilent()
                return
            }

            if (this.selectedDataMode === this.dataModes.LIVE_HISTORY.value) {
                this.refreshVisibleLiveHistoryVehicle()
                return
            }

            this.stopDataPolling()
        },
        startDataPolling() {
            this.stopDataPolling()

            if (!this.shouldRunDataPolling()) {
                return
            }

            this.dataPollIntervalId = setInterval(() => {
                this.tickDataPoll()
            }, LIVE_POLL_INTERVAL_MS)
        },
        stopDataPolling() {
            if (this.dataPollIntervalId) {
                clearInterval(this.dataPollIntervalId)
                this.dataPollIntervalId = null
            }
            this.dataPollInFlight = false
        },
        getLoadedHistoryVehicle(vehicleSerial) {
            return this.historyVehicles.find((item) => item.vehicleSerial === vehicleSerial) ?? null
        },
        clearHistoryResults() {
            this.historyVehicles = []
            this.loadingVehicleSerial = null
            this.stopDataPolling()
            this.clearMapOverlays()
        },
        async showVehicleOnMap(machineId, { fitBounds = true } = {}) {
            const vehicle = this.historyVehicles.find((item) => item.machineId === machineId)
            if (!vehicle) {
                return false
            }

            const isNewVehicle = this.visibleOnMapMachineId !== machineId

            if (isNewVehicle) {
                this.clearMapOverlays()
                this.visibleOnMapMachineId = machineId
            }

            await this.drawHistoryRoute(vehicle)
            this.ensureGpsMapClickListener()

            if (fitBounds) {
                this.fitRouteBounds(vehicle.points)
            }

            if (this.selectedDataMode === this.dataModes.LIVE_HISTORY.value) {
                this.startDataPolling()
            } else {
                this.stopDataPolling()
            }

            return true
        },
        hideVehicleFromMap() {
            this.clearMapOverlays()
        },
        getLiveVehicleTooltip({ object }) {
            if (!object) {
                return null
            }

            const label = object.machine_id ?? object.machineId ?? object.vehicleSerial
            if (label == null || label === '') {
                return null
            }

            return { text: String(label) }
        },
        async refreshLiveMarkersOverlay() {
            const mapStore = useMaps()
            if (!mapStore.map || !this.isLiveMarkersMode()) {
                this.clearLiveOverlay()
                return false
            }

            const livePoints = this.liveVehicles
                .filter((entry) => entry.points?.length)
                .map((entry) => ({
                    ...entry.points[0],
                    vehicleSerial: entry.vehicleSerial,
                    machineId: entry.machineId,
                }))

            if (!livePoints.length) {
                this.clearLiveOverlay()
                mapStore.showFieldFills()
                return false
            }

            mapStore.hideFieldFills()

            const { atlas, mapping } = await ensureVehicleFieldModeIcon()

            const openLiveVehicleInfo = (info) => {
                if (!info?.object) {
                    return
                }

                this.openPointInfoWindow(info.object, {
                    lat: info.object.latitude,
                    lng: info.object.longitude,
                })
            }

            const layer = new IconLayer({
                id: 'vehicle-live-icons',
                data: livePoints,
                pickable: true,
                billboard: true,
                iconAtlas: atlas,
                iconMapping: mapping,
                getIcon: (point) => this.vehicleIconFromSteerMode(point.steermode),
                getPosition: (point) => [point.longitude, point.latitude],
                getSize: (point) => this.vehicleIconMapSize(point.steermode, LIVE_VEHICLE_ICON_SIZE_PX),
                sizeUnits: 'pixels',
                getAngle: (point) => fieldModeIconAngle(point),
                onClick: openLiveVehicleInfo,
            })

            const overlayProps = {
                layers: [layer],
                getTooltip: (info) => this.getLiveVehicleTooltip(info),
                style: { zIndex: 1000 },
            }

            if (!this.liveOverlay) {
                this.liveOverlay = new GoogleMapsOverlay(overlayProps)
                this.liveOverlay.setMap(mapStore.map)
                this.ensureGpsMapClickListener()
            } else {
                this.liveOverlay.setProps(overlayProps)
            }

            return true
        },
        /** Single-vehicle live marker for as-applied (reuses /machine/.../positions + icon overlay). */
        async refreshSingleLiveVehicleSilent(vehicleSerial) {
            if (vehicleSerial == null || vehicleSerial === '') {
                return false
            }

            this.liveOnFieldsMap = true
            const ok = await this.loadLiveVehicle(vehicleSerial, { silent: true })
            if (!ok || !this.getLiveVehicle(vehicleSerial)?.points?.length) {
                this.clearLiveOverlay()
                return false
            }

            this.liveVehicles = this.liveVehicles.filter(
                (item) => item.vehicleSerial === vehicleSerial,
            )
            return this.refreshLiveMarkersOverlay()
        },
        clearSingleLiveVehicle(vehicleSerial) {
            if (vehicleSerial != null && vehicleSerial !== '') {
                this.liveVehicles = this.liveVehicles.filter(
                    (item) => item.vehicleSerial !== vehicleSerial,
                )
                delete this.liveVehicleStatus[vehicleSerial]
            }

            if (!this.liveOnVehicleTrackingPage && this.liveVehicles.length === 0) {
                this.liveOnFieldsMap = false
                this.clearLiveOverlay()
                useMaps().showFieldFills()
                this.closePointInfoWindow()
                this.removeGpsMapClickListener()
                return
            }

            if (this.isLiveMarkersMode()) {
                this.refreshLiveMarkersOverlay()
            } else {
                this.clearLiveOverlay()
            }
        },
        clearLiveOverlay() {
            if (this.liveOverlay) {
                this.liveOverlay.setMap(null)
            }
            this.liveOverlay = null
        },
        async drawHistoryRoute(vehicle) {
            const mapStore = useMaps()
            if (!mapStore.map || !mapStore.google || !vehicle?.points?.length) {
                return false
            }

            const points = vehicle.points
            const pointCount = points.length
            const arrowPoints = this.buildRouteArrowPoints(points)
            const routeArrowPoints = pointCount > 1 ? arrowPoints.slice(0, -1) : arrowPoints
            const endVehiclePoint = pointCount > 1 ? [arrowPoints[pointCount - 1]] : []
            const { atlas: arrowAtlas, mapping: arrowMapping } = getVehicleGpsArrowIcon()
            const { atlas: vehicleAtlas, mapping: vehicleMapping } = await ensureVehicleFieldModeIcon()

            mapStore.hideFieldFills()

            const openPointInfo = (info) => {
                if (!info?.object) {
                    return
                }
                this.openPointInfoWindow(info.object, {
                    lat: info.object.latitude,
                    lng: info.object.longitude,
                })
            }

            const layers = [
                new PathLayer({
                    id: `vehicle-route-path-${vehicle.machineId}`,
                    data: [{
                        path: points.map((point) => [point.longitude, point.latitude]),
                    }],
                    getPath: (item) => item.path,
                    getColor: [15, 23, 42, 180],
                    getWidth: 3,
                    widthUnits: 'pixels',
                    pickable: false,
                }),
                new IconLayer({
                    id: `vehicle-route-arrows-${vehicle.machineId}`,
                    data: routeArrowPoints,
                    pickable: true,
                    billboard: true,
                    iconAtlas: arrowAtlas,
                    iconMapping: arrowMapping,
                    getIcon: () => 'arrow',
                    getPosition: (point) => [point.longitude, point.latitude],
                    getColor: (point, { index }) => hexToRgba(
                        this.getGpsPointColor(point),
                        0.95,
                    ),
                    getSize: (_, { index }) => (index === 0 ? 30 : 18),
                    sizeUnits: 'pixels',
                    getAngle: (point) => bearingToIconAngle(
                        point.segmentBearing ?? point.heading ?? 0,
                    ),
                    onClick: openPointInfo,
                }),
            ]

            if (endVehiclePoint.length) {
                layers.push(new IconLayer({
                    id: `vehicle-route-end-${vehicle.machineId}`,
                    data: endVehiclePoint,
                    pickable: true,
                    billboard: true,
                    iconAtlas: vehicleAtlas,
                    iconMapping: vehicleMapping,
                    getIcon: (point) => this.vehicleIconFromSteerMode(point.steermode),
                    getPosition: (point) => [point.longitude, point.latitude],
                    getSize: (point) => this.vehicleIconMapSize(
                        point.steermode,
                        HISTORY_END_VEHICLE_ICON_SIZE_PX,
                    ),
                    sizeUnits: 'pixels',
                    getAngle: (point) => fieldModeIconAngle(point),
                    onClick: openPointInfo,
                }))
            }

            if (!this.routeOverlay) {
                this.routeOverlay = new GoogleMapsOverlay({
                    layers,
                })
                this.routeOverlay.setMap(mapStore.map)
            } else {
                this.routeOverlay.setProps({ layers })
            }

            return true
        },
        fitRouteBounds(points) {
            const mapStore = useMaps()
            if (!mapStore.map || !mapStore.google || !points?.length) {
                return
            }

            const bounds = new mapStore.google.maps.LatLngBounds()
            points.forEach((point) => {
                bounds.extend({ lat: point.latitude, lng: point.longitude })
            })

            mapStore.map.fitBounds(bounds, 40)
        },
        clearGpsInfoWindowContent() {
            if (this.gpsInfoWindowEl) {
                render(null, this.gpsInfoWindowEl)
            }
        },
        closePointInfoWindow() {
            if (this.gpsInfoWindow) {
                this.gpsInfoWindow.close()
            }
            this.clearGpsInfoWindowContent()
        },
        ensureGpsMapClickListener() {
            const mapStore = useMaps()
            if (this.gpsMapClickListener || !mapStore.map || !mapStore.google) {
                return
            }

            this.gpsMapClickListener = mapStore.google.maps.event.addListener(
                toRaw(mapStore.map),
                'click',
                () => this.closePointInfoWindow(),
            )
        },
        removeGpsMapClickListener() {
            if (!this.gpsMapClickListener) {
                return
            }

            const mapStore = useMaps()
            mapStore.google?.maps.event.removeListener(this.gpsMapClickListener)
            this.gpsMapClickListener = null
        },
        enrichPointForInfoWindow(point) {
            if (point?.vehicleSerial == null) {
                return point
            }

            const entry = this.getLiveVehicle(point.vehicleSerial)
            if (!entry?.payload) {
                return point
            }

            return {
                ...point,
                is_online: entry.payload.is_online ?? null,
                last_seen: entry.payload.last_seen ?? point.last_seen,
            }
        },
        openPointInfoWindow(point, position) {
            const mapStore = useMaps()
            if (!mapStore.map || !mapStore.google) {
                return
            }

            this.closePointInfoWindow()

            if (!this.gpsInfoWindowEl) {
                this.gpsInfoWindowEl = document.createElement('div')
            }

            const displayPoint = this.enrichPointForInfoWindow(point)
            const vnode = createVNode(VehiclePositionInfoWindow, { point: displayPoint })
            vnode.appContext = app._context
            render(vnode, this.gpsInfoWindowEl)

            if (!this.gpsInfoWindow) {
                this.gpsInfoWindow = new mapStore.google.maps.InfoWindow()
                mapStore.google.maps.event.addListener(this.gpsInfoWindow, 'closeclick', () => {
                    this.clearGpsInfoWindowContent()
                })
            }

            this.gpsInfoWindow.setContent(this.gpsInfoWindowEl)
            this.gpsInfoWindow.setPosition(position)
            this.gpsInfoWindow.open({ map: toRaw(mapStore.map) })
            this.ensureGpsMapClickListener()
        },
        clearMapOverlays() {
            const mapStore = useMaps()

            this.stopDataPolling()
            this.closePointInfoWindow()
            this.removeGpsMapClickListener()
            if (this.legendDraggable) {
                this.legendDraggable.disable()
            }
            this.visibleOnMapMachineId = null
            mapStore.showFieldFills()

            if (this.routeOverlay) {
                this.routeOverlay.setMap(null)
            }
            this.routeOverlay = null

            this.clearLiveOverlay()
            this.clearAllFromRequestedPath()
        },
        centerMarkerOnMap(vehicleSerial) {
            const mapStore = useMaps()
            const point = this.liveVehicles.find((item) => item.vehicleSerial === vehicleSerial)?.points?.[0]

            if (!mapStore.map || !point) {
                return
            }

            const map = toRaw(mapStore.map)
            map.panTo({ lat: point.latitude, lng: point.longitude })

            if (map.getZoom() < 15) {
                map.setZoom(15)
            }
        },
        clearAllFromRequestedPath() {
            this.requestedIsobusTaskId = null
            this.paths = []
            this.hidePath()
        },
        async showPath(pathId) {
            const ps = usePlanner()

            this.selectedPathId = pathId
            const path = await ps.showPath(pathId)
        },
        hidePath() {
            const ps = usePlanner()

            this.selectedPathId = null
            ps.clearMarkers(false)
        },
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useVehiclesLiveStore, import.meta.hot))
}
